import React, { useState, useEffect, useContext } from 'react';
import {
  FaStar,
  FaCheckCircle,
  FaBan,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilter,
} from 'react-icons/fa';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import AdminReviewDetailModal from '../../../components/admin/AdminReviewsManagement/AdminReviewDetailModal';
import '../../../assets/css/admin/adminReviewsManagement/adminreviewsmanagement.css';

const AdminReviewsManagement = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
    rating: 'all',
  });

  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    fetchReviews();
  }, [baseUrl, authToken]);

  const fetchReviews = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/admin/reviews`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews(res.data.data || []);
        } else {
          showToast(res.data.message || 'Failed to fetch reviews', 'error');
        }
      })
      .catch(() => showToast('Unable to connect to server', 'error'))
      .finally(() => setLoading(false));
  };

  const handleApprove = (id) => {
    setActionLoading(id);

    axios
      .post(`${baseUrl}/admin/reviews/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, status: 'approved' } : r
            )
          );
          showToast('Review approved', 'success');
        }
      })
      .finally(() => setActionLoading(null));
  };

  const handleReject = (id) => {
    setActionLoading(id);

    axios
      .post(`${baseUrl}/admin/reviews/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, status: 'rejected' } : r
            )
          );
          showToast('Review rejected', 'success');
        }
      })
      .finally(() => setActionLoading(null));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure?')) return;

    setActionLoading(id);

    axios
      .delete(`${baseUrl}/admin/reviews/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews((prev) => prev.filter((r) => r.id !== id));
          showToast('Review deleted', 'success');
        }
      })
      .finally(() => setActionLoading(null));
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const filteredReviews = reviews.filter((review) => {
    const q = filters.search.toLowerCase();

    const matchSearch =
      !q ||
      String(review.review_id || '').toLowerCase().includes(q) ||
      review.jjbooking?.jjcustomer?.cus_username?.toLowerCase().includes(q);

    const matchStatus =
      filters.status === 'all' || review.status === filters.status;

    const matchType =
      filters.type === 'all' || review.jjbooking?.booking_type === filters.type;

    const matchRating =
      filters.rating === 'all' ||
      Math.floor(review.rating) === parseInt(filters.rating);

    return matchSearch && matchStatus && matchType && matchRating;
  });

  const stats = {
    total: reviews.length,
    pending: reviews.filter((r) => r.status === 'pending').length,
    approved: reviews.filter((r) => r.status === 'approved').length,
    rejected: reviews.filter((r) => r.status === 'rejected').length,
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <FaStar
        key={i}
        className={i < Math.floor(rating) ? 'adm-rev-star-filled' : 'adm-rev-star-empty'}
      />
    ));

  return (
    <div className="adm-rev-container">

      <div className="adm-rev-header">
        <h1>Reviews Management</h1>
        <p>Manage and moderate customer reviews</p>
      </div>

      {/* STATS */}
      <div className="adm-rev-stats-grid">
        <div className="adm-rev-stat-card"><FaStar /><h3>{stats.total}</h3><p>Total</p></div>
        <div className="adm-rev-stat-card"><FaFilter /><h3>{stats.pending}</h3><p>Pending</p></div>
        <div className="adm-rev-stat-card"><FaCheckCircle /><h3>{stats.approved}</h3><p>Approved</p></div>
        <div className="adm-rev-stat-card"><FaBan /><h3>{stats.rejected}</h3><p>Rejected</p></div>
      </div>

      {/* TABLE */}
      <div className="adm-rev-table-container">

        {loading ? (
          <p>Loading...</p>
        ) : filteredReviews.length === 0 ? (
          <p>No reviews found</p>
        ) : (
          <table className="adm-rev-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer</th>
                <th>Booking</th>
                <th>Type</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
  {filteredReviews.map((review) => (
    <tr key={review.id}>

      <td>{review.review_id}</td>

      <td>
        <div className="adm-rev-customer">
          <strong>{review.jjbooking?.jjcustomer?.cus_username}</strong>
          <br />
          <small>{review.jjbooking?.jjcustomer?.cus_email}</small>
        </div>
      </td>

      <td>B-{review.jjbooking_id}</td>

      <td>{review.jjbooking?.booking_type}</td>

      <td>
        <div className="adm-rev-stars">
          {renderStars(review.rating)}
        </div>
      </td>

      <td>
        <span className={`adm-rev-status-badge adm-rev-status-${review.status}`}>
          {review.status}
        </span>
      </td>

      <td>
        <div className="adm-rev-actions">

          <button onClick={() => {
            setSelectedReview(review);
            setShowModal(true);
          }}>
            <FaEye />
          </button>

          {review.status === 'pending' && (
            <>
              <button onClick={() => handleApprove(review.id)}>
                <FaCheckCircle />
              </button>

              <button onClick={() => handleReject(review.id)}>
                <FaBan />
              </button>
            </>
          )}

          <button onClick={() => handleDelete(review.id)}>
            <FaTrash />
          </button>

        </div>
      </td>

    </tr>
  ))}
</tbody>
          </table>
        )}
      </div>

      {/* MODAL */}
      {showModal && selectedReview && (
        <AdminReviewDetailModal
          review={selectedReview}
          onClose={() => {
            setShowModal(false);
            setSelectedReview(null);
          }}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDelete}
          actionLoading={actionLoading}
        />
      )}

      {/* TOAST */}
      {toast.show && (
        <div className={`adm-rev-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

    </div>
  );
};

export default AdminReviewsManagement;